import { createTheme } from '@mui/material/styles';

export type ColorMode = 'light' | 'dark';

// Monochrome ops theme: hierarchy carried by contrast and weight instead of color, in either
// direction. Both palettes name the same roles so every override below reads from one shape.
const DARK = {
  bg: '#0a0a0a',
  paper: '#111213',
  paper2: '#1a1b1d',
  line: '#26282b',
  text: '#f5f6f7',
  dim: '#9aa0a6',
  faint: '#5f6368'
};

const LIGHT = {
  bg: '#f6f7f8',
  paper: '#ffffff',
  paper2: '#eef0f2',
  line: '#d7dade',
  text: '#101214',
  dim: '#5b6167',
  faint: '#8b9198'
};

export const palettes: Record<ColorMode, typeof DARK> = { dark: DARK, light: LIGHT };

export const buildTheme = (mode: ColorMode = 'dark') => {
  const MONO = palettes[mode];
  return createTheme({
  palette: {
    mode,
    primary: { main: MONO.text, contrastText: MONO.bg },
    secondary: { main: MONO.dim },
    background: { default: MONO.bg, paper: MONO.paper },
    divider: MONO.line,
    text: { primary: MONO.text, secondary: MONO.dim },
    success: { main: MONO.text, contrastText: MONO.bg },
    info: { main: MONO.text },
    warning: { main: MONO.dim },
    error: { main: MONO.text }
  },
  shape: { borderRadius: 10 },
  typography: {
    fontFamily: "-apple-system, 'Segoe UI', Roboto, 'Helvetica Neue', sans-serif",
    h6: { fontWeight: 650, letterSpacing: '-0.01em' },
    subtitle2: { letterSpacing: '0.08em', fontWeight: 600 },
    button: { textTransform: 'none', fontWeight: 600 }
  },
  components: {
    MuiCssBaseline: {
      styleOverrides: {
        body: { fontVariantNumeric: 'tabular-nums' }
      }
    },
    MuiPaper: {
      defaultProps: { elevation: 0 },
      styleOverrides: {
        root: {
          backgroundImage: 'none',
          border: `1px solid ${MONO.line}`
        }
      }
    },
    MuiAppBar: {
      styleOverrides: {
        root: {
          backgroundColor: MONO.bg,
          borderBottom: `1px solid ${MONO.line}`,
          boxShadow: 'none'
        }
      }
    },
    MuiTableCell: {
      styleOverrides: {
        root: { borderBottomColor: MONO.line },
        head: {
          color: MONO.dim,
          fontSize: 12,
          fontWeight: 600,
          textTransform: 'uppercase',
          letterSpacing: '0.06em',
          whiteSpace: 'nowrap'
        },
        stickyHeader: { backgroundColor: MONO.paper }
      }
    },
    MuiTableRow: {
      styleOverrides: {
        root: {
          '&.MuiTableRow-hover:hover': { backgroundColor: MONO.paper2 }
        }
      }
    },
    MuiTabs: {
      styleOverrides: {
        indicator: { backgroundColor: MONO.text, height: 2 }
      }
    },
    MuiTab: {
      styleOverrides: {
        root: {
          color: MONO.dim,
          '&.Mui-selected': { color: MONO.text }
        }
      }
    },
    MuiButton: {
      styleOverrides: {
        containedPrimary: {
          '&:hover': { backgroundColor: MONO.dim }
        },
        outlined: { borderColor: MONO.line }
      }
    },
    MuiOutlinedInput: {
      styleOverrides: {
        notchedOutline: { borderColor: MONO.line }
      }
    },
    MuiAccordion: {
      defaultProps: { elevation: 0, disableGutters: true },
      styleOverrides: {
        root: {
          border: `1px solid ${MONO.line}`,
          '&::before': { display: 'none' }
        }
      }
    },
    MuiLinearProgress: {
      styleOverrides: {
        root: { backgroundColor: MONO.paper2, borderRadius: 99, height: 6 },
        bar: { backgroundColor: MONO.text, borderRadius: 99 }
      }
    },
    MuiChip: {
      styleOverrides: {
        root: { fontWeight: 600 }
      }
    },
    MuiAlert: {
      styleOverrides: {
        root: { border: `1px solid ${MONO.line}` }
      }
    },
    MuiTablePagination: {
      styleOverrides: {
        // The pager is wider than a phone; let it wrap onto two lines rather than scroll.
        toolbar: ({ theme }) => ({
          [theme.breakpoints.down('sm')]: {
            flexWrap: 'wrap',
            justifyContent: 'flex-end',
            paddingLeft: theme.spacing(1),
            rowGap: theme.spacing(0.5)
          }
        }),
        spacer: ({ theme }) => ({
          [theme.breakpoints.down('sm')]: { display: 'none' }
        })
      }
    }
  }
  });
};

export const theme = buildTheme('dark');

export default theme;
